import { categories, timeIntervals } from '../constants/options';

export const mapBackendToForm = (data, uhid_no) => {
  if (!data) return null;

  const timeKeyMap = {
    "15 min": "time_15",
    "30 min": "time_30",
    "45 min": "time_45",
    "1 hr": "time_1h",
    "2 hr": "time_2h",
    "3 hr": "time_3h",
    "4 hr": "time_4h",
  };

  const checkboxStates = categories.map((cat, catIndex) =>
    timeIntervals.map((time, timeIndex) => {
      if (timeIndex < 3) return false;
      const keySuffix = timeKeyMap[time];
      if (!keySuffix) return false;
      const catKey = cat.name.toLowerCase();
      return Boolean(data[`${catKey}_${keySuffix}`]);
    })
  );

  const optionsFromBackend = [
    data.circulation_option || "",
    data.oxygenation_option || "",
    data.respiration_option || "",
    data.consciousness_option || "",
    data.activity_option || ""
  ];

  const selectedOptions = categories.flatMap((cat, catIndex) =>
    timeIntervals.map((time, timeIndex) => {
      if (time === "Pre-Anesthesia Vital Signs") {
        return optionsFromBackend[catIndex];
      }
      return "";
    })
  );

  const scores = categories.map((cat, catIndex) =>
    timeIntervals.map((time, timeIndex) => {
      if (time === "Pre-Anesthesia Vital Signs") {
        const opt = optionsFromBackend[catIndex];
        const idx = cat.options.findIndex(o => o === opt);
        return idx >= 0 ? idx : 0;
      }
      return 0;
    })
  );

  const baseTime = categories.map((cat) => data[`${cat.name.toLowerCase()}_adm_time`] || "");
  const admTime = categories.map((cat) => data[`${cat.name.toLowerCase()}_adm`] || "");
  const dischargeTime = categories.map((cat) => data[`${cat.name.toLowerCase()}_discharge_time`] || "");

  const totalScores = [
    data.pre_anesthesia_vitalscore || 0,
    0, // index 1 (Time)
    0, // index 2 (Adm)
    data.score_15 || 0,
    data.score_30 || 0,
    data.score_45 || 0,
    data.score_1h || 0,
    data.score_2h || 0,
    data.score_3h || 0,
    data.score_4h || 0,
    0, // Discharge
  ];

  return {
    patient_name: data.patient_name || "",
    age: data.age || "",
    gender: data.gender || "",
    uhid_no: data.uhid || uhid_no,
    surgery_procedure: data.surgery_procedure || "",
    anaesthesia: data.anaesthesia || "",
    scores,
    selectedOptions,
    checkboxStates,
    baseTime,
    admTime,
    dischargeTime,
    totalScores,
  };
};

export const mapFormToBackend = (formData) => {
  return {
    patient_name: formData.patient_name,
    hospital_id: 1,
    age: formData.age,
    gender: formData.gender,
    uhid_no: formData.uhid_no,
    surgery_procedure: formData.surgery_procedure,
    anaesthesia: formData.anaesthesia,

    // Pre-Anesthesia Category Options
    circulation_option: formData.selectedOptions[0],
    oxygenation_option: formData.selectedOptions[timeIntervals.length * 1 + 0],
    respiration_option: formData.selectedOptions[timeIntervals.length * 2 + 0],
    consciousness_option: formData.selectedOptions[timeIntervals.length * 3 + 0],
    activity_option: formData.selectedOptions[timeIntervals.length * 4 + 0],

    // Discharge Times
    circulation_discharge_time: formData.dischargeTime[0],
    oxygenation_discharge_time: formData.dischargeTime[1],
    respiration_discharge_time: formData.dischargeTime[2],
    consciousness_discharge_time: formData.dischargeTime[3],
    activity_discharge_time: formData.dischargeTime[4],

    // Adm Scores
    circulation_adm: formData.admTime[0],
    oxygenation_adm: formData.admTime[1],
    respiration_adm: formData.admTime[2],
    consciousness_adm: formData.admTime[3],
    activity_adm: formData.admTime[4],

    // Adm Times
    circulation_adm_time: formData.baseTime[0],
    oxygenation_adm_time: formData.baseTime[1],
    respiration_adm_time: formData.baseTime[2],
    consciousness_adm_time: formData.baseTime[3],
    activity_adm_time: formData.baseTime[4],

    // Time-based Scores and Times
    circulation_time_15: formData.checkboxStates[0][3],
    oxygenation_time_15: formData.checkboxStates[1][3],
    respiration_time_15: formData.checkboxStates[2][3],
    consciousness_time_15: formData.checkboxStates[3][3],
    activity_time_15: formData.checkboxStates[4][3],

    circulation_time_30: formData.checkboxStates[0][4],
    oxygenation_time_30: formData.checkboxStates[1][4],
    respiration_time_30: formData.checkboxStates[2][4],
    consciousness_time_30: formData.checkboxStates[3][4],
    activity_time_30: formData.checkboxStates[4][4],

    circulation_time_45: formData.checkboxStates[0][5],
    oxygenation_time_45: formData.checkboxStates[1][5],
    respiration_time_45: formData.checkboxStates[2][5],
    consciousness_time_45: formData.checkboxStates[3][5],
    activity_time_45: formData.checkboxStates[4][5],

    circulation_time_1h: formData.checkboxStates[0][6],
    oxygenation_time_1h: formData.checkboxStates[1][6],
    respiration_time_1h: formData.checkboxStates[2][6],
    consciousness_time_1h: formData.checkboxStates[3][6],
    activity_time_1h: formData.checkboxStates[4][6],

    circulation_time_2h: formData.checkboxStates[0][7],
    oxygenation_time_2h: formData.checkboxStates[1][7],
    respiration_time_2h: formData.checkboxStates[2][7],
    consciousness_time_2h: formData.checkboxStates[3][7],
    activity_time_2h: formData.checkboxStates[4][7],

    circulation_time_3h: formData.checkboxStates[0][8],
    oxygenation_time_3h: formData.checkboxStates[1][8],
    respiration_time_3h: formData.checkboxStates[2][8],
    consciousness_time_3h: formData.checkboxStates[3][8],
    activity_time_3h: formData.checkboxStates[4][8],

    circulation_time_4h: formData.checkboxStates[0][9],
    oxygenation_time_4h: formData.checkboxStates[1][9],
    respiration_time_4h: formData.checkboxStates[2][9],
    consciousness_time_4h: formData.checkboxStates[3][9],
    activity_time_4h: formData.checkboxStates[4][9],

    // Total Scores
    pre_anesthesia_vitalscore: formData.totalScores[0],
    score_15: formData.totalScores[3],
    score_30: formData.totalScores[4],
    score_45: formData.totalScores[5],
    score_1h: formData.totalScores[6],
    score_2h: formData.totalScores[7],
    score_3h: formData.totalScores[8],
    score_4h: formData.totalScores[9]
  };
};
